import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../../contexts/APIContext";
import logger from "../../utils/logger";

const MAX_RECOMMENDATIONS = 6;
const FEED_LIMIT = 10;
const FEED_OFFSET = 0;
const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

/**
 * Recommended Storybooks — rendered beneath the storybook reading page.
 * Fetches public storybooks, hides the current one, displays up to 6 in a
 * horizontally-scrollable shelf with the brand picture-book treatment.
 */
const RecommendedStorybooks = ({ currentStorybookId = null }) => {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const [storybooks, setStorybooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeed = async () => {
      if (!apiClient) return;
      try {
        setLoading(true);
        const response = await apiClient.getPublicStorybooksFeed(FEED_LIMIT, FEED_OFFSET);
        const raw = response?.data ?? [];
        const items = Array.isArray(raw) ? raw : [];
        const publicOnly = items.filter((item) => item.is_public === true);
        const excluded = currentStorybookId
          ? publicOnly.filter((item) => item.id !== currentStorybookId)
          : publicOnly;
        setStorybooks(excluded.slice(0, MAX_RECOMMENDATIONS));
      } catch (err) {
        logger.error("RecommendedStorybooks: fetch failed", err);
        setStorybooks([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFeed();
  }, [apiClient, currentStorybookId]);

  const handleCardClick = (id) => {
    if (id) navigate(`/storybooks/${id}`);
  };

  // Section heading
  const Heading = () => (
    <div className="mb-3">
      <span
        className="inline-block mb-0.5"
        style={{
          ...FREDOKA,
          color: "#0065F8",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        Keep reading
      </span>
      <h2
        style={{
          ...FREDOKA,
          color: "#0B1A33",
          fontWeight: 700,
          fontSize: 22,
          letterSpacing: "-0.01em",
          textTransform: "uppercase",
          margin: 0,
        }}
      >
        More storybooks
      </h2>
    </div>
  );

  if (loading) {
    return (
      <section
        className="px-4 py-6 md:px-6 lg:px-8 [scrollbar-width:none] [&_*::-webkit-scrollbar]:hidden"
        aria-label="Recommended Storybooks"
      >
        <Heading />
        <div className="flex gap-4 overflow-x-auto pb-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="animate-pulse min-w-[140px] max-w-[140px] flex-shrink-0"
              style={{
                aspectRatio: "2/3",
                background: "rgba(11,26,51,0.06)",
                borderRadius: 12,
              }}
            />
          ))}
        </div>
      </section>
    );
  }

  if (storybooks.length === 0) {
    return (
      <section
        className="px-4 py-6 md:px-6 lg:px-8"
        aria-label="Recommended Storybooks"
      >
        <Heading />
        <p
          className="text-center py-8"
          style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14 }}
        >
          No more storybooks to recommend right now — check back soon!
        </p>
      </section>
    );
  }

  return (
    <section
      className="px-4 py-6 md:px-6 lg:px-8 [&_*::-webkit-scrollbar]:hidden"
      aria-label="Recommended Storybooks"
    >
      <Heading />
      <div
        className="flex gap-4 pb-2"
        style={{
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {storybooks.map((item) => {
          const id = item.id;
          const thumbnailUrl =
            item.cover_url ?? item.cover_image_url ?? item.thumbnail_url ?? null;
          const title = item.title || "Untitled Storybook";
          const views = item.views ?? 0;
          return (
            <button
              key={id}
              type="button"
              onClick={() => handleCardClick(id)}
              className="group text-left flex-shrink-0 transition-all duration-200 hover:-translate-y-1"
              style={{
                ...FREDOKA,
                width: 160,
                background: "#FFFFFF",
                border: "2px solid rgba(11,26,51,0.08)",
                borderRadius: 14,
                boxShadow:
                  "0px 2px 0px rgba(11,26,51,0.04), 0px 6px 14px rgba(11,26,51,0.06)",
                overflow: "hidden",
                scrollSnapAlign: "start",
                cursor: "pointer",
              }}
            >
              <div
                className="relative"
                style={{
                  aspectRatio: "2/3",
                  background: "#EAF3FF",
                }}
              >
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(0,101,248,0.15), rgba(0,202,255,0.15))",
                      color: "#0065F8",
                    }}
                  >
                    <svg width="38" height="38" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
                    </svg>
                  </div>
                )}
                {/* Title bar */}
                <div
                  className="absolute inset-x-0 bottom-0 px-2.5 py-2"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(11,26,51,0.85) 0%, rgba(11,26,51,0.45) 55%, transparent 100%)",
                  }}
                >
                  <p
                    style={{
                      ...FREDOKA,
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 12,
                      letterSpacing: "-0.01em",
                      lineHeight: 1.15,
                      margin: 0,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {title}
                  </p>
                </div>
              </div>
              <div className="p-2 flex items-center gap-1.5">
                <svg width="13" height="13" fill="none" stroke="#5A6B85" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span style={{ ...FREDOKA, color: "#5A6B85", fontSize: 11, fontWeight: 600 }}>
                  {views} {views === 1 ? "view" : "views"}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default RecommendedStorybooks;
